import { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { WeightUnity } from '../../types/weight-unity.type';
import { UpdateWeightUnityRequestDTO } from '../../types/dtos/update-weight-unity.dto';

const WEIGHT_UNITY_NAME_INPUT_ID = 'edit-weight-unity-name';
const WEIGHT_UNITY_ABBREVIATION_INPUT_ID = 'edit-weight-unity-abbreviation';

type Props = {
  open: boolean;
  weightUnity: WeightUnity | null;
  isSubmitting: boolean;
  onSubmit: (data: UpdateWeightUnityRequestDTO) => Promise<void> | void;
  onClose: () => void;
};

const useStyles = makeStyles((theme) => ({
  paper: {
    minWidth: 400,
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    paddingTop: '0.5rem',

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  saveButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

export function WeightUnityDialog({ open, weightUnity, isSubmitting, onSubmit, onClose }: Props) {
  const classes = useStyles();

  const [name, setName] = useState(weightUnity?.name ?? '');
  const [abbreviation, setAbbreviation] = useState(weightUnity?.abbreviation ?? '');

  if (!weightUnity) return null;

  const trimmedName = name.trim();
  const trimmedAbbreviation = abbreviation.trim();

  const changes: UpdateWeightUnityRequestDTO = {};
  if (trimmedName !== weightUnity.name) changes.name = trimmedName;
  if (trimmedAbbreviation !== weightUnity.abbreviation) changes.abbreviation = trimmedAbbreviation;

  const hasChanges = Object.keys(changes).length > 0;
  const isValid = trimmedName !== '' && trimmedAbbreviation !== '';
  const canSubmit = hasChanges && isValid && !isSubmitting;

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(changes);
  };

  return (
    <Dialog open={open} onClose={handleClose} classes={{ paper: classes.paper }} disableEscapeKeyDown={isSubmitting}>
      <DialogTitle>Modifier l&apos;unité de poids</DialogTitle>
      <DialogContent>
        <div className={classes.fields}>
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            required
            id={WEIGHT_UNITY_NAME_INPUT_ID}
            label="Nom"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            required
            id={WEIGHT_UNITY_ABBREVIATION_INPUT_ID}
            label="Abréviation"
            value={abbreviation}
            onChange={(event) => setAbbreviation(event.target.value)}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button size="small" variant="outlined" onClick={handleClose} disabled={isSubmitting}>
          Annuler
        </Button>
        <Button
          size="small"
          variant="contained"
          className={classes.saveButton}
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
        >
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
